import React, { useEffect, useState, useContext } from "react";

import * as notify from "../../../../shared/notify";
import { Input, ButtonInput } from "../../../../components/form-inputs/inputs";
import { useInput } from "../../../../shared/hooks/form-fields";
import eventEmitter from "../../../../shared/events";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortAlphaDown, faSortAlphaUp, faArchive, faDownload } from "@fortawesome/free-solid-svg-icons";

import classnames from "classnames";
import "./picklist.scss";

import AttributePagination from "./picklist-paging";
import { getPicklistData, addPicklistItem, archivePicklistItem } from "./data";
import { getCSV } from "shared/data";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";

//Added actions for fetch, create, archive and button click events.
const ActionOnAttributes = ({ ...props }) => {
    const { viewOnlyAdmin } = useContext(ViewOnlyPermissionsContext);
    //States
    const [attributeData, setAttributeData] = useState({
        all: [],
        active: [],
        archived: [],
    });
    const [isSubmitting, setIsSubmitting] = useState(false); //is create is in progress
    const [isFetching, setIsFetching] = useState(false); //is fetching is in progress
    const [IsDescendingSort, setDescendingSort] = useState(false); //if order is descending selected
    const [showArchived, setShowArchived] = useState(false); //To show Archive grid
    const [searchedValue, setSearchedValue] = useState(null); //To bind filtered text
    const [currentPage, setCurrentPage] = useState(0); //Active grid current page index
    const [perPage, setPerPage] = useState(5); //Active grid per page "number of rows" selection
    const [archiveCurrentPage, setArchiveCurrentPage] = useState(0); //Archive grid current page index
    const [archivePerPage, setArchivePerPage] = useState(5); //Archive grid per page "number of rows" selection

    const attributeInput = useInput(""); //create
    const searchInput = useInput(""); //search

    //Fetch Attribute List
    useEffect(() => {
        if (searchedValue) {
            eventEmitter.on("PICKLIST_ADDED", fetchPicklistData);
            fetchPicklistData();
            setCurrentPage(0);
        } else {
            setAttributeData({
                all: [],
                active: [],
                archived: [],
            });
        }
    }, [searchedValue, IsDescendingSort]);

    const fetchPicklistData = async () => {
        setIsFetching(true);
        let data = null;
        let param = {
            name: searchedValue,
            isDesc: IsDescendingSort,
            type: `${props.name}`,
        };

        data = await getPicklistData(param);

        setAttributeData({
            all: data,
            active: data.filter((n) => n.isActive === true),
            archived: data.filter((n) => n.isActive === false),
        });
        setIsFetching(false);
    };

    //To search attribute by entered text
    const searchAttribute = async () => {
        if (!searchInput.value || searchInput.value.length < 1) {
            setSearchedValue(null);
            notify.error("Name requires min 1 character(s) to search");

            return;
        }

        setSearchedValue(searchInput.value);
    };

    //To set sort type
    const sortList = async () => {
        setDescendingSort(!IsDescendingSort);
    };

    //To show/hide Archive list on "Show Archive" click
    const toggleArchives = () => {
        setShowArchived(!showArchived);
    };

    //To Create an attribute (brand or manufacturer)
    const onAdd = async () => {
        if (isSubmitting) {
            return;
        }

        let hasError = false;

        if (!attributeInput.value) {
            notify.error(`Please enter a ${props.attrName} name`);
            hasError = true;
        }

        if (hasError) {
            return;
        }

        setIsSubmitting(true);
        let param = {
            text: attributeInput.value,
            type: `${props.name}`,
        };

        const settings = {
            noRedirect: true,
        };

        const response = await addPicklistItem(param, settings);

        if (!response) {
            setIsSubmitting(false);

            return;
        }

        if (response && response.success === false) {
            setIsSubmitting(false);

            return notify.error("You do not have permission to perform this action.");
        }

        notify.success(`${props.attrName} name "${attributeInput.value}" added successfully`);
        attributeInput.reset();

        eventEmitter.emit("PICKLIST_ADDED");
        setIsSubmitting(false);
    };

    //On archiving an existing attribute
    const onArchive = async (item) => {
        let param = {
            text: item.text,
            tag: item.tagMaskValue,
            isactive: false,
            type: `${props.name}`,
        };

        const response = await archivePicklistItem(param);

        if (!response) {
            return;
        }

        if (response && response.success === false) {
            return notify.error("You do not have permission to perform this action.");
        }

        notify.success(`"${item.text}" is archived successfully`);

        fetchPicklistData();

        const archivedAttribute = attributeData.all.find((n) => n.text === response.text);

        if (!archivedAttribute) {
            return;
        }

        setAttributeData({
            all: attributeData.all,
            active: attributeData.active.filter((n) => n.text !== response.text),
            archived: [{ ...archivedAttribute }, ...attributeData.archived],
        });
    };

    //To handle Active list pagination
    const handlePagination = ({ selected: selectedPage }) => {
        setCurrentPage(selectedPage);
    };

    //To handle Archive list pagination
    const handleArchivePagination = ({ selected: selectedPage }) => {
        setArchiveCurrentPage(selectedPage);
    };

    //To update selected page size for Active list
    const updatePageSize = (e) => {
        setPerPage(parseInt(e.target.value));
    };

    //To update selected page size for Archive list
    const updateArchivePageSize = (e) => {
        setArchivePerPage(parseInt(e.target.value));
    };

    const handleDownload = async () => {
        const exportContains = searchedValue ? `&contains=${searchedValue}` : "";
        const file = await getCSV(`/picklists/picklist-reporting?name=${props.name}${exportContains}`);

        if (!file) {
            return notify.error("You do not have permission to perform this action.");
        }

        return file;
    };

    return (
        <>
            <div className="contentWidth">
                <div className="button-group">
                    <button
                        className="button -primary"
                        title={`Download ${props.attrName} List`}
                        onClick={handleDownload}
                        disabled={viewOnlyAdmin[`download${props.attrName}s`]}
                    >
                        <FontAwesomeIcon icon={faDownload} />
                    </button>
                    <ButtonInput
                        className="picklist-search"
                        name="attributeId"
                        {...searchInput.bind}
                        buttonClick={searchAttribute}
                        buttonText={`Search ${props.attrName}`}
                    />
                    <button className="button -primary -right picklist-sort" title={`Sort ${props.attrName} Alphabetically`} onClick={sortList}>
                        <FontAwesomeIcon icon={IsDescendingSort ? faSortAlphaUp : faSortAlphaDown} />
                    </button>
                </div>
            </div>

            <section className="picklist-border-box contentWidth">
                <div className="search-group input-group">
                    <Input {...attributeInput.bind} name="add-attribute" type="text" label={`${props.attrName} Name`} />
                    <button
                        title={`Add new ${props.attrName} name`}
                        className={classnames("button -primary", "picklist-btnAttribute", { "-disabled": isSubmitting })}
                        onClick={() => onAdd(props.attrName)}
                        disabled={viewOnlyAdmin[`create${props.attrName}`]}
                    >{`Add ${props.attrName}`}</button>
                </div>
            </section>

            <br />
            {attributeData.active.length === 0 && searchedValue && !isFetching && <h2 className="txt-center">No matches found!</h2>}
            {attributeData.active.length > 0 && (
                <AttributePagination
                    attributes={attributeData.active}
                    isArchiveList={false}
                    archiveAttribute={onArchive}
                    currentPage={currentPage}
                    perPage={perPage}
                    handlePagination={handlePagination}
                    updatePageSize={updatePageSize}
                    viewOnlyUpdate={viewOnlyAdmin[`update${props.attrName}`]}
                />
            )}
            <br />
            <br />
            {attributeData.archived.length > 0 && (
                <div>
                    <button className="button -primary" title={`Show/Hide ${props.attrName} Archive List`} onClick={toggleArchives}>
                        {" "}
                        Show Archived <FontAwesomeIcon className="picklist-archiveIcon" icon={faArchive} />
                    </button>
                </div>
            )}
            {showArchived && attributeData.archived.length > 0 && (
                <AttributePagination
                    attributes={attributeData.archived}
                    isArchiveList={true}
                    archiveCurrentPage={archiveCurrentPage}
                    archivePerPage={archivePerPage}
                    handleArchivePagination={handleArchivePagination}
                    updateArchivePageSize={updateArchivePageSize}
                />
            )}
            <br />
        </>
    );
};

export default ActionOnAttributes;
